import { Component, OnDestroy, OnInit } from '@angular/core';
import { ReportsService } from '../reports.service';
import { GlobalSpinnerService } from 'src/app/shared/global-spinner/global-spinner.service';
import { AppService } from 'src/app/services/app.service';
import { Subject } from 'rxjs';
import { CommonModule, KeyValue, formatDate } from '@angular/common';
import { IReportParameters, RenderFormat, PerformancePeriod } from '../reports-models';
import { finalize, takeUntil, tap } from 'rxjs/operators';
import { UtilsService } from 'src/app/shared/utils.service';
import { DomSanitizer } from '@angular/platform-browser';
import { LocalizationService, LocalizationSettings } from 'src/app/shared/localization/localization.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageToUiService } from 'src/app/services/message-to-ui.service';
import { SettingsService } from 'src/app/services/settings.service';
import { FormsModule } from '@angular/forms';
import { CustomSelectComponent } from 'src/app/shared/custom-select/custom-select.component';
import { CustomMultiSelectComponent } from 'src/app/shared/custom-multi-select/custom-multi-select.component';
import { DatePickerModule } from 'primeng/datepicker';

@Component({
  selector: 'app-performance',
  templateUrl: './performance.component.html',
  styleUrls: ['./performance.component.scss'],
  imports: [CommonModule, TranslateModule, DatePickerModule, FormsModule, CustomSelectComponent, CustomMultiSelectComponent]
})
export class PerformanceComponent implements OnInit, OnDestroy {
  customerNumber: number;
  selectedPeriod: number;
  periodOptions: KeyValue<any, string>[];
  locale: LocalizationSettings = {} as LocalizationSettings;
  startDate: Date;
  endDate: Date;
  canEditDate: boolean;
  userAssignedCustomersCount: number;
  customers$ = this.appService.customersSelectItems$
    .pipe(tap(_ => {
      this.userAssignedCustomersCount = _.length;
      if (this.userAssignedCustomersCount == 1)
        {this.selectedCustomers = [_[0].key]; }
    }));

  selectedCustomers: number[] = [];

  showResults: boolean;
  renderedReport: any;
  private readonly unsubscribe$ = new Subject();
  private language: string;
  private reportFolder: string;

  constructor(
    private appService: AppService,
    private reportsService: ReportsService,
    private localization: LocalizationService,
    private spinnerService: GlobalSpinnerService,
    private utilsService: UtilsService,
    private _sanitizer: DomSanitizer,
    private translateService: TranslateService,
    private notificationService: MessageToUiService,
    private settingsService: SettingsService
  ) { }

  ngOnInit(): void {
    this.spinnerService.show();

    this.periodOptions = this.utilsService.enumToKeyValuePair(PerformancePeriod, 'Reports.Performance');
    this.selectedPeriod = this.periodOptions[0].key;
    this.endDate = this.getLastDayOfPreviousMonth();
    this.startDate = this.getFirstDayOfFirstMonth(12);

    this.localization.settings$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(result => this.locale = result);

    this.localization.currentLanguage$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(result => this.language = result);

    this.settingsService.getSettingValueByName('ReportServer_FolderForKreditorWeb')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(_ => this.reportFolder = _);

    this.spinnerService.hide();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  onChangePeriod() {
    if (this.selectedPeriod == PerformancePeriod.Custom) {
      this.canEditDate = true;
    }
    else {
      this.endDate = this.getLastDayOfPreviousMonth();
      const offset = (this.selectedPeriod == PerformancePeriod.Last12 ? 12 : this.selectedPeriod == PerformancePeriod.Last6 ? 6 : 3);
      this.startDate = this.getFirstDayOfFirstMonth(offset);
      this.canEditDate = false;
    }
  }

  onGenerateReport() {
    const parameters = {
      templatePath: `${this.reportFolder}Performance`,
      displayName: this.translateService.instant('Reports.Performance.Title'),
      format: RenderFormat.Pdf,
      parameters: {
        StartDate: formatDate(this.startDate, 'yyyy-MM-dd', this.locale.culture),
        EndDate: formatDate(this.endDate, 'yyyy-MM-dd', this.locale.culture),
        Language: this.language.capitalize()
      }
    } as IReportParameters;

    if (this.selectedCustomers.length > 0) {
      parameters.parameters.CustomerNumbers = this.selectedCustomers.join(',').toString();
    }

    this.spinnerService.show();

    this.reportsService.renderReport(parameters)
      .pipe(finalize(() => {
        this.spinnerService.hide();
      }))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (result) => {
          const blobData = this.utilsService.b64toBlob(result.data, 'application/pdf');
          this.renderedReport = this._sanitizer.bypassSecurityTrustResourceUrl(window.URL.createObjectURL(blobData));
          this.showResults = true;
        },
        error: () => this.notificationService.genericError()
      })
  }

  private getLastDayOfPreviousMonth(): Date {
    const d = new Date();
    d.setDate(1);
    d.setHours(-1);
    return d;
  }

  private getFirstDayOfFirstMonth(offset: number): Date {
    const d = new Date();
    d.setMonth(d.getMonth() - offset);
    d.setDate(1);
    return d;
  }
}
